import { DatePipe } from '@angular/common';
import { Component, computed, effect, inject, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TNil } from '@app/app.interface';
import { SnackbarService } from '@core/services/snackbar.service';
import { WindowRedirectService } from '@core/services/window-redirect.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { DotsSpinnerComponent } from '@shared/components/dots-spinner/dots-spinner.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SideDialogComponent } from '@shared/components/side-dialog/side-dialog.component';
import { IUserInvitation } from '@shared/interfaces/user-invitation.interface';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { isAfter } from 'date-fns';
import { filter } from 'rxjs/operators';
import { ICreatorInvitationDialogData, TInvitationActionType } from './creator-invitation-dialog.interface';
import { CreatorInvitationDialogStore } from './creator-invitation-dialog.store';

@Component({
  imports: [
    SideDialogComponent,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    TranslateModule,
    DotsSpinnerComponent,
    MatErrorComponent,
    DatePipe,
    IconComponent,
  ],
  providers: [CreatorInvitationDialogStore],
  selector: 'app-creator-invitation-dialog',
  templateUrl: './creator-invitation-dialog.component.html',
})
export class CreatorInvitationDialogComponent {
  public readonly invitationForm = new FormGroup({
    email: new FormControl<string>('', [Validators.required, Validators.email]),
    message: new FormControl<string>('', [Validators.required]),
  });

  public readonly invitationExpired = computed(() => {
    return (
      this.creatorInvitation()?.expire &&
      isAfter(new Date(), this.dateUtils.parseDateTimeFromApi(this.creatorInvitation()!.expire))
    );
  });
  public readonly invitationExpiryDate = computed(() => {
    return this.creatorInvitation()?.expire
      ? this.dateUtils.parseDateTimeFromApi(this.creatorInvitation()!.expire)
      : null;
  });

  public creatorInvitation!: WritableSignal<IUserInvitation | TNil>;

  public readonly store = inject(CreatorInvitationDialogStore);
  public readonly data = inject<ICreatorInvitationDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<CreatorInvitationDialogComponent>);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translate = inject(TranslateService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly dialog = inject(MatDialog);

  private actionType: TInvitationActionType = null;

  constructor() {
    this.initialize();
    this.createInvitationEffect();
    this.createFormEffect();
    this.createEmailUsedForAnotherCreatorEffect();
    this.createCannotInviteSelfEffect();
    this.createEmailUsedInDifferentGroupEffect();
  }

  public onCloseClick(): void {
    this.dialogRef.close();
  }

  public onSubmit(): void {
    delete this.invitationForm.controls.email.errors?.['emailUsedForAnotherCreator'];
    this.invitationForm.controls.email.updateValueAndValidity();

    if (this.invitationForm.invalid) {
      this.invitationForm.markAllAsTouched();

      return;
    }

    if (this.creatorInvitation()?.createdUserId) {
      return;
    }

    this.actionType = 'send';

    this.store.sendInvitation({
      creatorId: this.data.creatorId,
      email: this.invitationForm.value.email!,
      message: this.invitationForm.value.message!,
    });
  }

  public onRevokeClick(): void {
    const creatorInvitationId = this.creatorInvitation()?.id;

    if (!creatorInvitationId) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        cancelText: this.translate.instant('pages.creatorDetails.creatorInvitation.deleteInvitation.cancelText'),
        confirmText: this.translate.instant('pages.creatorDetails.creatorInvitation.deleteInvitation.confirmText'),
        message: this.translate.instant('pages.creatorDetails.creatorInvitation.deleteInvitation.message'),
        title: this.translate.instant('pages.creatorDetails.creatorInvitation.deleteInvitation.title'),
      },
      width: '400px',
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.performRevoke(creatorInvitationId);
      });
  }

  public performRevoke(creatorInvitationId: string): void {
    this.actionType = 'revoke';

    this.store.revokeInvitation({
      id: creatorInvitationId,
    });
  }

  public onOpenErrorCreatorClick(): void {
    const creatorId = this.store.emailUsedForAnotherCreatorId$();

    if (creatorId) {
      WindowRedirectService.openUrlInNewTab(`/creators/${creatorId}`);
    }
  }

  private initialize(): void {
    this.creatorInvitation = this.data.updatedInvitation;

    this.invitationForm.patchValue({
      email: this.data.creatorInvitation?.email ?? '',
      message:
        this.data.creatorInvitation?.message ??
        this.translate.instant('pages.creatorDetails.creatorInvitation.dialog.defaultMessage'),
    });
  }

  private createInvitationEffect(): void {
    effect(() => {
      if (
        !this.store.loading$() &&
        this.actionType &&
        (!this.store.error$() || this.store.error$() === this.store.ALREADY_INVITED_ERROR_CODE)
      ) {
        if (this.actionType === 'send') {
          this.creatorInvitation.set(this.store.creatorInvitation$());

          if (this.store.error$() !== this.store.ALREADY_INVITED_ERROR_CODE) {
            this.snackbarService.openSuccess(
              this.translate.instant('pages.creatorDetails.creatorInvitation.dialog.invitationSent'),
            );

            this.onCloseClick();
          }
        }

        if (this.actionType === 'revoke') {
          this.creatorInvitation.set(this.store.creatorInvitation$());

          this.snackbarService.openSuccess(
            this.translate.instant('pages.creatorDetails.creatorInvitation.dialog.invitationRevoked'),
          );

          this.onCloseClick();
        }
      }
    });
  }

  private createFormEffect(): void {
    effect(() => {
      if (this.creatorInvitation()?.createdUserId) {
        this.invitationForm.disable();
      } else {
        this.invitationForm.enable();
      }
    });
  }

  private createEmailUsedForAnotherCreatorEffect(): void {
    effect(() => {
      if (this.store.emailUsedForAnotherCreatorName$()) {
        this.invitationForm.controls.email.setErrors({
          emailUsedForAnotherCreator: { creatorName: this.store.emailUsedForAnotherCreatorName$() },
        });
      }
    });
  }

  private createCannotInviteSelfEffect(): void {
    effect(() => {
      if (this.store.cannotInviteSelf$()) {
        this.invitationForm.controls.email.setErrors({ cannotInviteSelf: true });
      }
    });
  }

  private createEmailUsedInDifferentGroupEffect(): void {
    effect(() => {
      if (this.store.emailUsedInDifferentGroup$()) {
        this.invitationForm.controls.email.setErrors({ emailUsedInDifferentGroup: true });
      }
    });
  }
}
